import { BaseDialogComponent } from './../../../../../components/kit/base-dialog/base-dialog.component';
import {
    PersonRestrictionEnum,
    personRestrictionEnumOptions,
} from '../../../../../../../../types/src/services/enum/person-restriction.enum';
import { Component, inject, OnInit, output, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { NgClass } from '@angular/common';
import { AbstractControl, AsyncValidatorFn, ReactiveFormsModule, Validators } from '@angular/forms';
import { fb } from '../../../../shared/shared.global-service';
import { dateify } from '../../../../shared/helpers/date.helper';
import { PersonRestrictionsDialogStore } from './person-restrictions-dialog.store';
import {
    AddressDetailObject,
    CreatePersonContactInput,
    CreateUserInput,
    PersonContactGridItemObject,
    UpdatePersonContactInput,
    UserGridItemObject,
} from '@it2go/types';
// import { PersonRestrictionsDialogStore2 } from './person-restrictions-dialog.store2';
import { contentGlobalErrors, contentGlobalSuccess, successCreated, successUpdated } from '../../../content.error';
import { removeTypename } from '../../../../shared/utils/object.utils';
import { FieldErrorFactory } from '../../../../shared/service/field-error.service';
import { FilterBuilder } from '../../../helper/filter.builder';
import { InnerModalComponent } from '../../../../shared/components/kit/inner-modal/inner-modal.component';
import { toSignal } from '@angular/core/rxjs-interop';
import { isInvalid } from '../../../../shared/helpers/validate';
import { PersonContactTypeEnum } from '../../../../../../../../types/src/services/enum/person-contact-type.enum';
import { formatPerson } from '@libs/shared/helpers/person.utils';
import { ActionButtonsComponent } from '../../../../../components/kit/action-buttons/action-buttons.component';
import { AddressInputComponent } from '../../../../../components/kit/input/address-input/address-input.component';
import { ButtonComponent } from '../../../../../components/kit/button/button.component';
import { CheckInputComponent } from '../../../../../components/kit/input/check-input/check-input.component';
import { DateInputComponent } from '../../../../../components/kit/input/date-input/date-input.component';
import { EmailInputComponent } from '../../../../../components/kit/input/text-input/email-input.component';
import { PhoneInputComponent } from '../../../../../components/kit/input/phone-input/phone-input.component';
import { SelectInputComponent } from '../../../../../components/kit/input/select-input/select-input.component';
import { TextInputComponent } from '../../../../../components/kit/input/text-input/text-input.component';
import { SelectUserComponent } from '../../common/select-user/select-user.component';
import { TranslatePipe } from '@ngx-translate/core';

export interface PersonRestrictionDialogData {
    restriction?: PersonContactGridItemObject | null;
    contactUser?: any | null;
    personId?: number | null;
}

@Component({
    selector: 'app-person-restrictions-dialog',
    templateUrl: './person-restrictions-dialog.component.html',
    styleUrls: ['./person-restrictions-dialog.component.scss'],
    providers: [
        PersonRestrictionsDialogStore,
        // PersonRestrictionsDialogStore2,
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ActionButtonsComponent,
        AddressInputComponent,
        ButtonComponent,
        CheckInputComponent,
        DateInputComponent,
        EmailInputComponent,
        InnerModalComponent,
        NgClass,
        PhoneInputComponent,
        ReactiveFormsModule,
        SelectInputComponent,
        SelectUserComponent,
        TextInputComponent,
        TranslatePipe,
    ],
})
export class PersonRestrictionsDialogComponent extends BaseDialogComponent implements OnInit {

    public readonly store = inject(PersonRestrictionsDialogStore);

    public restriction: PersonContactGridItemObject | null = null;

    public personId: number | null = null;

    public created = output<any>();

    protected selectedUserName: string | null = null;

    @ViewChild('selectExistingModal') selectExistingModal!: InnerModalComponent;

    protected form = fb.group({
        validFrom: fb.control<Date | null>(null, [Validators.required]),
        validTo: fb.control<Date | null>(null, []),
        restrictionType: fb.control<string | null>(null, [Validators.required]),

        existingUserId: fb.control<string | null>(null),
        titlesBefore: fb.control<string | null>(null),
        name: fb.control<string | null>(null, [Validators.required]),
        surname: fb.control<string | null>(null, [Validators.required]),
        titlesAfter: fb.control<string | null>(null),
        mobile: fb.control<string | null>(null),
        phone: fb.control<string | null>(null),

        email: fb.control<string | null>(
            null,
            [Validators.email],
            [this.distinctEmail()],
        ),
        address: fb.control<AddressDetailObject | null>(null),

        basis: fb.control<string | null>(null),
        referenceNumber: fb.control<string | null>(null),
        isRelative: fb.control<boolean>(false),

        municipality: fb.control<number | null>(null),

        note: fb.control<string | null>(null),
    });

    protected selectedUserId = toSignal(this.form.controls['existingUserId'].valueChanges);

    public async ngOnInit() {
        this.restriction = this.dialogConfig.data?.restriction;
        this.personId = this.dialogConfig.data?.personId;

        if (this.restriction) {
            this.selectedUserName = formatPerson(this.restriction.user)

            this.form.patchValue({
                validFrom: dateify(this.restriction.validFrom),
                validTo: dateify(this.restriction.validTo),
                restrictionType: this.restriction.subType,


                titlesBefore: this.restriction.user.titleBeforeName,
                name: this.restriction.user.name,
                surname: this.restriction.user.surname,
                titlesAfter: this.restriction.user.titleAfterName,
                mobile: this.restriction.user.mobile,
                phone: this.restriction.user.phone,
                email: this.restriction.user.email,
                address: this.restriction.user.address,
                existingUserId: this.restriction.user.id,

                basis: this.restriction.data?.basis || null,
                referenceNumber: this.restriction.data?.referenceNumber || null,
                isRelative: this.restriction.data?.isRelative || false,

                municipality: this.restriction.data?.municipality || null,

                note: this.restriction.note,
            });
            this.form.get('titlesBefore')?.disable();
            this.form.get('name')?.disable();
            this.form.get('surname')?.disable();
            this.form.get('titlesAfter')?.disable();
            this.form.get('mobile')?.disable();
            this.form.get('phone')?.disable();
            this.form.get('email')?.disable();
            this.form.get('address')?.disable();
        }
    }

    private async isEmailAlreadyUsed(
        email: string | null,
    ) {
        if (email && email.length) {
            const users = await this.store.api.getUsers(FilterBuilder.filter({ email }));
            return !!users?.['items'].length;
        }

        return false;
    }

    private distinctEmail(): AsyncValidatorFn {
        return async (control: AbstractControl) => {
            if (this.selectedUserId() || !control.value) {
                return null;
            }

            return (await this.isEmailAlreadyUsed(control.value))
                ? FieldErrorFactory.emailAlreadyUsedError()
                : null;
        };
    }

    updateFormWithSelectedUser(user: UserGridItemObject | null) {
        if (this.selectExistingModal) {
            this.selectExistingModal.show(false);
        }

        this.selectedUserName = formatPerson(user)

        this.form.patchValue({
            existingUserId: user?.id ?? null,
            titlesBefore: user?.titleBeforeName ?? null,
            name: user?.name ?? null,
            surname: user?.surname ?? null,
            titlesAfter: user?.titleAfterName ?? null,
            email: user?.email ?? null,
            phone: user?.phone ?? null,
            mobile: user?.mobile ?? null,
            address: removeTypename(user?.address) ?? null,
        });
    }

    protected async select(e: any) {
        if (!this.selectedUserId()) {
            return;
        }

        const res = await this.store.api.getUsers(FilterBuilder.filter({
            id: this.selectedUserId(),
        }));

        this.updateFormWithSelectedUser(res?.items?.[0]);
    }

    protected deselect() {
        this.updateFormWithSelectedUser(null);
    }

    public override async save() {
        if (this.restriction) {
            await this.updatePersonRestriction();
        } else {
            await this.savePersonRestriction();
        }
    }

    public async updatePersonRestriction() {
        if (isInvalid(this.form)) return;

        const restriction: UpdatePersonContactInput = {
            id: this.restriction!.id,
            subType: this.form.value.restrictionType!,
            validFrom: this.form.value.validFrom!.toISOString(),
            validTo: this.form.value.validTo?.toISOString() || null,
            note: this.form.value.note || null,
            data: {
                basis: this.form.value.basis,
                referenceNumber: this.form.value.referenceNumber,
                isRelative: this.form.value.isRelative,
                municipality: this.form.value.municipality,
            },
        };

        this.personId && await this.store.api.updatePersonContact(restriction);
        successUpdated()
        this.dialogRef.close(restriction);
    }

    public async savePersonRestriction() {
        if (isInvalid(this.form)) return;

        let userId = this.selectedUserId()

        if (!userId) {
            const userInput: CreateUserInput = {
                email: this.form.value.email || null,
                name: this.form.value.name || '',
                surname: this.form.value.surname || '',
                titleBeforeName: this.form.value.titlesBefore || null,
                titleAfterName: this.form.value.titlesAfter || null,
                mobile: this.form.value.mobile || null,
                phone: this.form.value.phone || null,
                address: removeTypename(this.form.value.address),
                password: null,
                doctor: false,
                icpOfDoctor: null,
                organizationId: this.store.globalGlobalStore.organizationStore.selectedId(),
            }
            const user = await this.store.api.createUser(userInput);

            userId = user?.id
        }

        if (userId && this.personId) {
            const restriction: CreatePersonContactInput = {
                userId: userId,
                folderId: null,
                personId: this.personId,
                type: PersonContactTypeEnum.RESTRICTION,
                subType: this.form.value.restrictionType!,
                validFrom: this.form.value.validFrom!.toISOString(),
                validTo: this.form.value.validTo?.toISOString() || null,
                note: this.form.value.note || null,
                relation: '',
                data: {
                    basis: this.form.value.basis || null,
                    referenceNumber: this.form.value.referenceNumber || null,
                    isRelative: this.form.value.isRelative || false,
                    municipality: this.form.value.municipality || null,
                },
            };

            await this.store.api.createPersonContact(restriction);
            successCreated()
            this.dialogRef.close(restriction);
        } else {
            contentGlobalErrors.next('common.error');
        }
    }

    protected readonly personRestrictionEnumOptions = personRestrictionEnumOptions;
    protected readonly RestrictionEnum = PersonRestrictionEnum;

}
